import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchDesignByCode, updateDesign, deleteDesign } from "../api/DesignAPIs";
import { colors } from "../constants/colors";
import { API_BASE_URL_V2 } from "../api/URL_Header";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const SkeletonLoader = () => {
  return (
    <div className="animate-pulse text-right">
      <div className="h-8 bg-gray-200 rounded mb-6 w-64"></div>
      <div className="space-y-4">
        <div className="h-32 bg-gray-200 rounded"></div>
        <div className="h-20 bg-gray-200 rounded"></div>
        <div className="h-20 bg-gray-200 rounded w-3/4"></div>
      </div>
    </div>
  );
};

const SearchDesignPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [searchCode, setSearchCode] = useState("");
  const [editingDesign, setEditingDesign] = useState(null);

  const {
    data: designData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["designByCode", searchCode],
    queryFn: () => fetchDesignByCode(searchCode),
    enabled: false, // مش هيشتغل تلقائي، هنشغله يدوي
    staleTime: Infinity,
    onError: () => {
      toast.error("لا يوجد تصميم بهذا الكود أو فشل في الجلب");
    },
  });

  const design = designData
    ? {
        id: designData.designId,
        name: designData.name || "Unnamed Product",
        imageUrl_1: designData.imageUrl_1 || "",
        // imageUrl_2: designData.imageUrl_2 || "",
        newPrice: designData.priceAfter || 0,
        oldPrice: designData.priceBefore || 0,
        inStock: designData.inStock !== undefined ? designData.inStock : true,
        hasOffer: designData.hasOffer || false,
        material: designData.material || "",
        description: designData.description || "",
        driveLink: designData.driveLink || "",
        code: designData.code || "",
        sales: designData.sales || 0,
      }
    : null;

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchCode.trim()) {
      toast.error("يرجى إدخال كود التصميم");
      return;
    }
    refetch(); // نشغل الـ query يدويًا
  };

  const handleImageChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditingDesign((prev) => ({
          ...prev,
          [field]: reader.result.split(",")[1],
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditingDesign((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateDesign(editingDesign.id, {
        name: editingDesign.name,
        priceBefore: editingDesign.oldPrice,
        priceAfter: editingDesign.newPrice,
        driveLink: editingDesign.driveLink,
        inStock: editingDesign.inStock,
        hasOffer: editingDesign.hasOffer,
        imageBase64_1: editingDesign.imageBase64_1,
        imageBase64_2: editingDesign.imageBase64_2,
      });

      toast.success("تم تحديث التصميم بنجاح!");
      setEditingDesign(null);
      refetch(); // نعيد جلب التصميم بعد التعديل
    } catch (err) {
      toast.error("فشل في تحديث التصميم");
      console.error(err);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("هل أنت متأكد أنك تريد حذف هذا التصميم نهائيًا؟")) {
      return;
    }

    try {
      await deleteDesign(design.id);
      toast.success("تم حذف التصميم بنجاح!");
      setSearchCode("");
      queryClient.invalidateQueries({ queryKey: ["designs"] }); // عشان يتحدث في الصفحات التانية
    } catch (err) {
      toast.error("فشل في حذف التصميم");
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4" dir="rtl">
      <ToastContainer />
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">البحث عن تصميم بالكود</h1>
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 font-medium"
          >
            العودة إلى الداشبورد
          </button>
        </div>

        <form onSubmit={handleSearch} className="mb-10">
          <div className="flex gap-4 max-w-2xl">
            <input
              type="text"
              placeholder="أدخل كود التصميم (مثل: G001)"
              value={searchCode}
              onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
              className="flex-1 p-4 text-lg border rounded-lg focus:outline-none focus:ring-4 focus:ring-orange-300"
            />
            <button
              type="submit"
              disabled={isLoading}
              className="bg-orange-500 text-white px-10 py-4 rounded-lg hover:bg-orange-600 font-bold text-lg shadow-lg disabled:opacity-70"
              style={{ backgroundColor: colors.orange }}
            >
              {isLoading ? "جاري البحث..." : "بحث"}
            </button>
          </div>
        </form>

        {isLoading ? (
          <SkeletonLoader />
        ) : error ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg text-center">
            حدث خطأ أثناء جلب البيانات. تأكد من الكود وحاول مرة أخرى.
          </div>
        ) : design ? (
          <div className="bg-white shadow-xl rounded-xl p-8 border">
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <h2 className="text-2xl font-bold text-orange-600 m-0">
              نتيجة البحث: <span className="font-mono bg-gray-100 px-3 py-1 rounded">{design.code}</span>
            </h2>

            {design.driveLink && (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(design.driveLink);
                  toast.success("تم نسخ الرابط بنجاح!");
                }}
                className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 font-medium shadow transition flex items-center gap-2 whitespace-nowrap"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                نسخ الرابط
              </button>
            )}
          </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* معلومات التصميم */}
              <div className="space-y-4">
                <p><strong className="text-gray-700">ID:</strong> <span className="text-lg">{design.id}</span></p>
                <p><strong className="text-gray-700">الاسم:</strong> <span className="text-lg">{design.name}</span></p>
                <p><strong className="text-gray-700">الكود:</strong> <span className="font-mono bg-gray-100 px-3 py-1 rounded">{design.code}</span></p>
                <p><strong className="text-gray-700">المادة:</strong> {design.material || "غير محددة"}</p>
                <p><strong className="text-gray-700">الوصف:</strong> {design.description || "لا يوجد"}</p>
                <p><strong className="text-gray-700">المبيعات:</strong> <span className="text-xl font-bold text-green-600">{design.sales}</span></p>

                <div className="mt-4">
                  <strong className="text-gray-700">السعر:</strong>
                  {design.oldPrice > design.newPrice && (
                    <span className="line-through text-gray-500 text-lg mr-3">{design.oldPrice} ج</span>
                  )}
                  <span className="text-2xl font-bold text-green-600">{design.newPrice} جنيه</span>
                </div>

                <div className="flex gap-6 mt-4">
                  <span className={`px-4 py-2 rounded-full text-sm font-medium ${design.inStock ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                    {design.inStock ? "متوفر في المخزون" : "غير متوفر"}
                  </span>
                  {design.hasOffer && (
                    <span className="px-4 py-2 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      عرض خاص
                    </span>
                  )}
                </div>

                {design.driveLink && (
                  <p className="mt-4">
                    <strong className="text-gray-700">رابط الدرايف:</strong>
                    <a
                      href={design.driveLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline hover:text-blue-800 mr-2 ml-5 font-medium"
                    >
                      فتح الرابط
                    </a>
                  </p>
                )}
              </div>

              {/* الصور */}
              <div className="flex flex-col items-center gap-6">
                {design.imageUrl_1 && (
                  <img
                    src={`${API_BASE_URL_V2}${design.imageUrl_1}`}
                    alt={design.name}
                    className="max-h-64 max-w-full object-contain rounded-lg shadow-md border" // صغرت من 80 → 64
                  />
                )}
                {/* {design.imageUrl_2 && (
                  <img
                    src={`${API_BASE_URL_V2}${design.imageUrl_2}`}
                    alt={`${design.name} - صورة إضافية`}
                    className="max-h-64 max-w-full object-contain rounded-lg shadow-md border" // نفس الحجم
                  />
                )} */}
                {!design.imageUrl_1 && !design.imageUrl_2 && (
                  <div className="h-48 w-48 bg-gray-200 rounded-lg flex items-center justify-center text-gray-500">
                    لا توجد صور
                  </div>
                )}
              </div>
            </div>

            {/* أزرار الإجراءات */}
            <div className="mt-10 flex gap-4 justify-center">
              <button
                onClick={() => setEditingDesign(design)}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 font-bold text-lg shadow-lg"
              >
                تعديل التصميم
              </button>
              <button
                onClick={handleDelete}
                className="bg-red-600 text-white px-8 py-4 rounded-lg hover:bg-red-700 font-bold text-lg shadow-lg"
              >
                حذف التصميم
              </button>
            </div>
          </div>
        ) : searchCode ? (
          <div className="text-center py-16">
            <p className="text-2xl text-gray-600">لا يوجد تصميم بهذا الكود: <strong>{searchCode}</strong></p>
            <p className="text-lg text-gray-500 mt-4">تأكد من الكود وحاول مرة أخرى</p>
          </div>
        ) : null}

        {/* Modal التعديل */}
        {editingDesign && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-screen overflow-y-auto p-8">
              <h2 className="text-2xl font-bold mb-6 text-center">تعديل التصميم: {editingDesign.code}</h2>
              <form onSubmit={handleUpdate}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-right mb-2 font-medium">اسم التصميم</label>
                    <input
                      type="text"
                      name="name"
                      value={editingDesign.name}
                      onChange={handleInputChange}
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-400"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-right mb-2 font-medium">الكود (لا يمكن تعديله)</label>
                    <input
                      type="text"
                      value={editingDesign.code}
                      readOnly
                      className="w-full p-3 border rounded-lg bg-gray-100"
                    />
                  </div>

                  <div>
                    <label className="block text-right mb-2 font-medium">السعر قبل الخصم</label>
                    <input
                      type="number"
                      name="oldPrice"
                      value={editingDesign.oldPrice}
                      onChange={handleInputChange}
                      className="w-full p-3 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-right mb-2 font-medium">السعر بعد الخصم</label>
                    <input
                      type="number"
                      name="newPrice"
                      value={editingDesign.newPrice}
                      onChange={handleInputChange}
                      className="w-full p-3 border rounded-lg"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-right mb-2 font-medium">رابط درايف</label>
                    <input
                      type="text"
                      name="driveLink"
                      value={editingDesign.driveLink}
                      onChange={handleInputChange}
                      className="w-full p-3 border rounded-lg"
                    />
                  </div>

                  <div className="flex items-center gap-8">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="inStock"
                        checked={editingDesign.inStock}
                        onChange={handleInputChange}
                        className="w-5 h-5"
                      />
                      <span className="font-medium">في المخزون</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="hasOffer"
                        checked={editingDesign.hasOffer}
                        onChange={handleInputChange}
                        className="w-5 h-5"
                      />
                      <span className="font-medium">عرض خاص</span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-right mb-2 font-medium">صورة 1 (جديدة)</label>
                    {editingDesign.imageUrl_1 && (
                      <img src={`${API_BASE_URL_V2}${editingDesign.imageUrl_1}`} alt="حالية" className="h-32 object-cover rounded mb-3" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, "imageBase64_1")}
                      className="w-full p-3 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-right mb-2 font-medium">صورة 2 (جديدة)</label>
                    {editingDesign.imageUrl_2 && (
                      <img src={`${API_BASE_URL_V2}${editingDesign.imageUrl_2}`} alt="حالية" className="h-32 object-cover rounded mb-3" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, "imageBase64_2")}
                      className="w-full p-3 border rounded-lg"
                    />
                  </div>
                </div>

                <div className="flex justify-center gap-6 mt-10">
                  <button
                    type="button"
                    onClick={() => setEditingDesign(null)}
                    className="bg-gray-400 text-white px-8 py-3 rounded-lg hover:bg-gray-500 font-bold"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="bg-orange-500 text-white px-10 py-3 rounded-lg hover:bg-orange-600 font-bold shadow-lg"
                    style={{ backgroundColor: colors.orange }}
                  >
                    حفظ التعديلات
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchDesignPage;